import { useEffect, useState } from 'react';
import { SearchInput } from '../../components/SearchInput';
import { useMachineFilters } from '../../hooks/useMachineFilters';
import { MachineViewToggle } from './MachineViewToggle';
import { MachineView } from './useMachineViewMode';

interface MachinesSearchBarProps {
  view: MachineView;
  /** The layouts this tier offers, in the order the toggle cycles them. */
  views: readonly MachineView[];
  onViewChange: (view: MachineView) => void;
}

/**
 * The search row under the app bar. It writes the same `displayName` filter the
 * filter panel writes, so the two can never disagree.
 */
export function MachinesSearchBar({
  view,
  views,
  onViewChange,
}: MachinesSearchBarProps) {
  const { filters, setDisplayName } = useMachineFilters();
  const [draft, setDraft] = useState(filters.displayName);
  const [synced, setSynced] = useState(filters.displayName);

  // Follow the filter panel and the back button.
  if (filters.displayName !== synced) {
    setSynced(filters.displayName);
    setDraft(filters.displayName);
  }

  // Debounce, so one URL write covers a burst of keystrokes.
  useEffect(() => {
    if (draft === filters.displayName) return;

    const timer = setTimeout(() => setDisplayName(draft), 300);
    return () => clearTimeout(timer);
  }, [draft, filters.displayName, setDisplayName]);

  return (
    <div
      className='border-border-secondary bg-surface-primary flex shrink-0
        items-center gap-2 border-y px-4 py-3'
    >
      <SearchInput
        value={draft}
        onChange={setDraft}
        placeholder='Search machines...'
        aria-label='Search machines'
        // Input drops its 44px minimum above `md`, which a tablet exceeds.
        className='[&>span]:text-primary flex-1 [&_input]:md:min-h-11'
      />
      <MachineViewToggle view={view} views={views} onChange={onViewChange} />
    </div>
  );
}
